<template>
	<div class="command-categories" style="height: 116px; min-height: 116px; max-height: 116px">
		<div class="category">
			<div class="category-actions" data-mark-category="标注">
				<div
					v-for="action in markActions"
					:key="action.key"
					class="mark-command"
					:class="{ active: isMarkActionActive(action) }"
					:data-mark-key="action.key"
					:data-mark-disabled="isMarkActionDisabled(action) ? '1' : '0'"
				>
					<DropdownButton
						:is-active="isMarkActionActive(action)"
						:disabled="isMarkActionDisabled(action)"
						:icon="getMarkActionIcon(action)"
						:label="getMarkActionLabel(action)"
						:hint="{
							image: action.hintImage,
							desc: action.hint,
						}"
						size="large"
						direction="column"
						:options="getMarkDropdownOptions(action)"
						@select="handleMarkSelect(action, $event)"
						@click="handleMarkClick(action)"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { h, ref } from "vue";
	import { storeToRefs } from "pinia";
	import type { DropdownOption } from "naive-ui";
	import DropdownButton from "@/components/common/DropdownButton.vue";
	import SvgIcon from "@/components/common/SvgIcon.vue";
	import { useToolbarCommandStore } from "@/stores/toolbar-command";
	import markAnnotationIcon from "@/assets/icon/mark_annotation.svg?raw";
	import markColorIcon from "@/assets/icon/mark_color.svg?raw";
	import markDrawIcon from "@/assets/icon/mark_draw.svg?raw";
	import markEraseIcon from "@/assets/icon/mark_erase.svg?raw";
	import markSelectIcon from "@/assets/icon/mark_select.svg?raw";
	import markTouchIcon from "@/assets/icon/mark_touch.svg?raw";
	import markNoteIcon from "@/assets/icon/mark_note.svg?raw";

	interface ToolbarAction {
		key: string;
		label: string;
		icon: string;
		command?: string;
		hint?: string;
		hintImage?: string;
		disabled?: boolean;
		modes?: ToolbarModeOption[];
	}

	interface ToolbarModeOption {
		mode: string;
		label: string;
		icon: string;
		command: string;
		hint: string;
		hintImage?: string;
		disabled?: boolean;
	}

	interface ToolbarOperation {
		name: string;
		hint: string;
	}

	const emit = defineEmits<{
		(e: "command", commandType: string): void;
		(e: "operationChange", operation: ToolbarOperation): void;
	}>();

	const toolbarCommandStore = useToolbarCommandStore();
	const { activeCommands } = storeToRefs(toolbarCommandStore);

	const markActions: ToolbarAction[] = [
		{
			key: "mark-annotation",
			label: "标注",
			icon: markAnnotationIcon,
			command: "mark-annotation",
			hint: "在零件、装配体或工程图中创建或编辑标注。",
		},
		{
			key: "mark-color",
			label: "颜色",
			icon: markColorIcon,
			command: "mark-color",
			hint: "指定标注的颜色和线宽。",
			disabled: true,
		},
		{
			key: "mark-draw",
			label: "绘制",
			icon: markDrawIcon,
			command: "mark-draw",
			hint: "在零件、装配体和工程图上创建标注。",
			disabled: true,
		},
		{
			key: "mark-erase",
			label: "擦除",
			icon: markEraseIcon,
			command: "mark-erase",
			hint: "从标注擦除墨迹笔划。",
			disabled: true,
		},
		{
			key: "mark-select",
			label: "选择",
			icon: markSelectIcon,
			command: "mark-select",
			hint: "在标注中选择墨迹笔划或注释。",
			disabled: true,
		},
		{
			key: "mark-touch",
			label: "触摸",
			icon: markTouchIcon,
			command: "mark-touch",
			hint: "切换触摸和鼠标输入。",
			disabled: true,
		},
		{
			key: "mark-note",
			label: "注释",
			icon: markNoteIcon,
			command: "mark-note",
			hint: "在标记中添加注释。",
			disabled: true,
		},
	];

	const markActiveMode = ref<Record<string, string>>({
		"mark-color": "mark-color",
	});

	function isCommandActive(command?: string): boolean {
		if (!command) return false;
		return activeCommands.value.includes(command);
	}

	function getMarkModeOptions(action: ToolbarAction): ToolbarModeOption[] {
		return action.modes ?? [];
	}

	function getMarkActiveOption(action: ToolbarAction): ToolbarModeOption | null {
		const options = getMarkModeOptions(action);
		if (options.length === 0) {
			return null;
		}
		const activeMode = markActiveMode.value[action.key] ?? options[0].mode;
		return options.find(option => option.mode === activeMode) ?? options[0];
	}

	function getMarkActionIcon(action: ToolbarAction): string {
		const activeOption = getMarkActiveOption(action);
		return activeOption?.icon ?? action.icon;
	}

	function getMarkActionLabel(action: ToolbarAction): string {
		const activeOption = getMarkActiveOption(action);
		return activeOption?.label ?? action.label;
	}

	function getMarkDropdownOptions(action: ToolbarAction): DropdownOption[] {
		return getMarkModeOptions(action).map(option => ({
			key: option.mode,
			label: option.label,
			disabled: option.disabled,
			icon: () => h(SvgIcon, { content: option.icon, alt: option.label, class: "dropdown-option-icon" }),
		}));
	}

	function isMarkActionDisabled(action: ToolbarAction): boolean {
		const activeOption = getMarkActiveOption(action);
		return Boolean(action.disabled || activeOption?.disabled);
	}

	function isMarkActionActive(action: ToolbarAction): boolean {
		const options = getMarkModeOptions(action);
		if (options.length === 0) {
			return !action.disabled && isCommandActive(action.command);
		}
		return !action.disabled && options.some(option => isCommandActive(option.command));
	}

	function emitOperation(name: string, hint: string): void {
		emit("operationChange", {
			name,
			hint,
		});
	}

	function handleMarkSelect(action: ToolbarAction, mode: string): void {
		if (action.disabled) {
			return;
		}
		const options = getMarkModeOptions(action);
		const selected = options.find(option => option.mode === mode);
		if (!selected) {
			return;
		}
		markActiveMode.value[action.key] = selected.mode;
		if (selected.disabled) {
			return;
		}
		emit("command", selected.command);
		emitOperation(selected.label, selected.hint);
	}

	function handleMarkClick(action: ToolbarAction): void {
		if (isMarkActionDisabled(action)) {
			return;
		}
		const activeOption = getMarkActiveOption(action);
		if (activeOption) {
			emit("command", activeOption.command);
			emitOperation(activeOption.label, activeOption.hint);
			return;
		}
		if (!action.command) {
			return;
		}
		emit("command", action.command);
		emitOperation(action.label, action.hint ?? "正在打开操作，请在视口中选择相关对象。");
	}
</script>

<style scoped lang="less">
	.command-categories {
		display: flex;
		align-items: stretch;
		gap: 0;
		flex-wrap: nowrap;
		width: 100%;
		height: 116px;
		min-height: 116px;
		max-height: 116px;
		box-sizing: border-box;
		overflow-x: auto;
	}

	.category {
		display: flex;
		flex-direction: column;
		gap: 6px;
		padding: 4px 8px;
		min-width: max-content;
		position: relative;
	}

	.category-actions {
		display: flex;
		align-items: stretch;
		gap: 2px;
	}

	.mark-command {
		display: inline-flex;
		align-items: stretch;
	}
</style>
